import { type MouseEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuid } from 'uuid';

import { company } from '~/company/Company';
import { useEstimatedTime } from '~/components/ActiveOrderCard/hooks';
import CloudflareResponseImage from '~/components/CloudflareResponsiveImage/CloudflareResponseImage';
import { defaultSrcSetParams } from '~/components/CloudflareResponsiveImage/constants';
import Icon from '~/components/Icon/Icon';
import { CDPrice as Price } from '~/components/Price/CDPrice';
import { mainStore } from '~/stores';
import capitalize from '~/utils/capitalize';
import copyToClipboard from '~/utils/copyToClipboard';
import { normalizeOrderId } from '~/utils/normalizeOrderId';

import type { MobileOrderCardProps } from './interfaces';
import styles from './MobileOrderCard.module.scss';
import { mapOrderName } from './utils';

const MobileOrderCard = ({ order, onClick }: MobileOrderCardProps) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const copyTimeoutRef = useRef<Nullable<ReturnType<typeof setTimeout>>>(null);

  const firstImage = order.items.at(0)?.image;
  const multipleItems = order.items.length > 1;
  const gallery = order.items.slice(0, 3);
  const absentCols: string[] =
    gallery.length < 3 ? new Array(3 - gallery.length).fill(uuid()) : [];
  const orderId = normalizeOrderId(order.order_id);

  const estimatedTime = useEstimatedTime(order);

  const handleCopy = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();

    copyToClipboard({
      text: orderId,
    });
    setCopied(true);

    copyTimeoutRef.current = setTimeout(() => {
      setCopied(false);
    }, 1000);
  };

  useEffect(
    () => () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
    },
    [],
  );

  return (
    <div
      className={styles.root}
      onClick={onClick}
      role="presentation"
      tabIndex={-1}
    >
      {!multipleItems && firstImage && (
        <div className={styles.imageWr}>
          <CloudflareResponseImage
            src={firstImage}
            srcParams={{ width: 98 }}
            srcSetParams={defaultSrcSetParams}
            alt={order.items.at(0)?.name}
            loading="lazy"
          />
        </div>
      )}
      {multipleItems && (
        <div className={styles.galleryGrid}>
          {gallery.map((item) => (
            <div className={styles.galleryGrid__imgCol} key={item.id}>
              <CloudflareResponseImage
                src={item.image}
                srcParams={{ width: 98 }}
                srcSetParams={defaultSrcSetParams}
                alt={order.items.at(0)?.name}
                loading="lazy"
              />
            </div>
          ))}
          {absentCols.map((key) => (
            <div key={key} className={styles.galleryGrid__imgCol} />
          ))}
          <div className={styles.galleryGrid__imgCol}>
            {order.items.length > 3 && `+${order.items.length - 3}`}
          </div>
        </div>
      )}
      <div className={styles.content}>
        <div className={styles.timeWr}>
          <span>
            {company.useStaticDeliveryEta
              ? t('phrases:staticDeliveryEta')
              : capitalize(estimatedTime)}
          </span>
          <Icon type="chevron-right" size={16} />
        </div>
        <div className={styles.statusRoot}>
          <span className={styles.status}>
            {t(mapOrderName(order.public_status))}
          </span>
        </div>
        <div className={styles.address}>{order.address?.address_1}</div>
        <div className={styles.priceWr}>
          <Price
            price={mainStore.convertPenceToPounds(order.paid_total ?? 0)}
            classes={{
              root: styles.price,
              classNameCurrentPrice: styles.currentPrice,
            }}
          />

          <div className={styles.orderIdWr} onClick={handleCopy}>
            <span>#{orderId}</span>
            <Icon type={copied ? 'check' : 'copy-alt'} size={12} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileOrderCard;
